import React from 'react';

import { validateLink } from '@aline/common';
import { InputLabel, Paper } from '@mui/material';
import { Formik } from 'formik';
import { AlineButton, defaultEditorPopoverPaperStyles, FormikAlineInput } from 'src/components';
import { ClientTransforms } from 'src/editor/transforms';
import { useEditor } from 'src/layout-engine';
import { makeStyles } from 'tss-react/mui';
import * as Yup from 'yup';

export enum LinkFormType {
  EDIT_LINK = 'EDIT_LINK',
  INSERT_LINK = 'INSERT_LINK',
}

const useStyles = makeStyles()(theme => ({
  buttonWrapper: {
    display: 'flex',
    justifyContent: 'flex-end',
    marginTop: theme.spacing(2),
  },
  field: {
    '&:first-child': {
      marginTop: 0,
    },
    marginTop: theme.spacing(2),
  },
  label: {
    color: theme.appColors.iconsType,
    fontSize: 12,
    fontWeight: 600,
    marginBottom: theme.spacing(1),
  },
  paper: {
    ...defaultEditorPopoverPaperStyles,
    padding: theme.spacing(2),
  },
}));

const LinkFormSchema = Yup.object().shape({
  href: Yup.string()
    .required('Required')
    .test('url', 'Please enter a valid URL (e.g., https://aline.co)', validateLink),
});

export interface LinkFormProps {
  closeLinkEditor: () => void;
  initialHref: string;
  initialText: string;
  onSubmit: (href: string, options: { modifiedLinkText?: string }) => void;
  type: LinkFormType;
  afterSubmit?: () => void;
  disabled?: boolean;
}

export function LinkForm({
  afterSubmit,
  closeLinkEditor,
  disabled,
  initialHref,
  initialText,
  onSubmit,
  type,
}: LinkFormProps): JSX.Element | null {
  const { classes } = useStyles();
  const editor = useEditor();

  return (
    <Paper className={classes.paper}>
      <Formik
        initialValues={{ href: initialHref, linkText: initialText }}
        validationSchema={LinkFormSchema}
        onSubmit={({ href, linkText }, { resetForm, setSubmitting }): void => {
          const address = href.replace(/\s/g, '');

          // Submit the relevant action from the EditLink or InsertLink Poppers.
          linkText !== initialText
            ? onSubmit(address, { modifiedLinkText: linkText })
            : onSubmit(address, {});

          if (afterSubmit) afterSubmit();
          closeLinkEditor();
          ClientTransforms.focus(editor);
          resetForm();
          setSubmitting(false);
        }}
      >
        {({ dirty, handleReset, handleSubmit, isSubmitting, submitForm }): JSX.Element => (
          <form onReset={handleReset} onSubmit={handleSubmit}>
            <div className={classes.field}>
              <InputLabel className={classes.label}>Text to Display</InputLabel>
              <FormikAlineInput
                disabled={disabled}
                fieldName="linkText"
                hideHelperText={true}
                inputProps={{
                  'data-cy': 'link-editor-text-input',
                }}
                onKeyDown={(event: React.KeyboardEvent): void => {
                  if (event.key === 'Enter') {
                    event.preventDefault();
                    submitForm();
                  }
                }}
              />
            </div>
            <div className={classes.field}>
              <InputLabel className={classes.label}>Link</InputLabel>
              <FormikAlineInput
                autoFocus
                fieldName="href"
                inputProps={{ 'data-cy': 'link-editor-href-input' }}
                onKeyDown={(event: React.KeyboardEvent): void => {
                  if (event.key === 'Enter') {
                    event.preventDefault();
                    submitForm();
                  }
                }}
              />
            </div>
            <div className={classes.buttonWrapper}>
              <AlineButton
                color="primary"
                data-cy="link-editor-apply-button"
                disabled={type === LinkFormType.INSERT_LINK ? isSubmitting : !dirty || isSubmitting}
                type="submit"
                variant="outlined"
              >
                Apply
              </AlineButton>
            </div>
          </form>
        )}
      </Formik>
    </Paper>
  );
}
